/**
 * Actions for logging events to an analytics service. Currently, Google
 * Analytics
 */

export const UPDATE_ANALYTICS_TRACKER = 'UPDATE_ANALYTICS_TRACKER';
export const ANALYTICS_HIT = 'ANALYTICS_HIT';
export const TIMING_METRIC = 'TIMING_METRIC';
export const SET_PAGE_VISIBILITY = 'SET_PAGE_VISIBILITY';
export const COMPLETE_RUN_ONCE_KPI = 'COMPLETE_RUN_ONCE_KPI';
export const START_PARALLEL_KPI = 'START_PARALLEL_KPI';
export const END_PARALLEL_KPI = 'END_PARALLEL_KPI';
export const START_SEQUENTIAL_KPI = 'START_SEQUENTIAL_KPI';
export const END_SEQUENTIAL_KPI = 'END_SEQUENTIAL_KPI';
export const INITIALISE_CONTINUOUS_KPI = 'INITIALISE_CONTINUOUS_KPI';
export const RESUME_CONTINUOUS_KPI = 'RESUME_CONTINUOUS_KPI';
export const PAUSE_CONTINUOUS_KPI = 'PAUSE_CONTINUOUS_KPI';
export const COMPLETE_CONTINUOUS_KPI = 'COMPLETE_CONTINUOUS_KPI';

// These are KPI names, *not* action types
export const kpiNames = {
    TIME_TO_INTERACTIVE: 'TIME_TO_INTERACTIVE',
    TIME_TO_APP_LOAD: 'TIME_TO_APP_LOAD',
    VIDEO_PLAYABLE: 'VIDEO_PLAYABLE',
    NAVIGATE_TO_REWARDS: 'NAVIGATE_TO_REWARDS',
    NAVIGATE_TO_INSIGHTS: 'NAVIGATE_TO_INSIGHTS',
    NAVIGATE_TO_FAQS: 'NAVIGATE_TO_FAQS',
    LOG_OUT: 'LOG_OUT',
    START_A_GAME: 'START_A_GAME',
    SPINNING_WHEEL: 'SPINNING_WHEEL',
    PAGE_REFRESH: 'PAGE_REFRESH',
    FETCH_ON_DEMAND_PACKAGE: 'FETCH_ON_DEMAND_PACKAGE',
};

/**
 * Update the current analytics tracker with the field/value pairs in the
 * fieldsObject
 * @param fieldsObject object containing the field/value pairs to update the
 * tracker with
 */
export function updateAnalyticsTracker(fieldsObject) {
    return {
        type: UPDATE_ANALYTICS_TRACKER,
        fieldsObject,
    };
}

/**
 * KPI actions. See performance-sagas.js for comments on how they work.
 */

/**
 * Send a hit to the analytics engine
 * @param hitType The type of the hit (e.g. event or pageview)
 * @param fieldsObject (optional) further field/value pairs describing the hit
 */
export function analyticsHit(hitType, fieldsObject) {
    if (!fieldsObject) {
        fieldsObject = null;
    }
    return { type: ANALYTICS_HIT, hitType, fieldsObject };
}

/**
 * completeRunOnceKpi
 * @param name - the name of the KPI.
 * @param fieldsObject (optional) further field/value pairs describing the hit
 */
export function completeRunOnceKpi(name) {
    return { type: COMPLETE_RUN_ONCE_KPI, name };
}

/**
 * startParallelKpi
 * @param name - the name of the KPI. Used to match the end event
 * @param key - the key of the KPI (eg an id) also used to match the end event
 */
export function startParallelKpi(name, key) {
    return { type: START_PARALLEL_KPI, name, key };
}

/**
 * endParallelKpi
 * @param name - the name of the KPI. Used to match the end event
 * @param key - the key of the KPI (eg an id) also used to match the start event
 * @param extraLabels (optional) further field/value pairs describing the hit
 */
export function endParallelKpi(name, key, extraLabels = null) {
    return { type: END_PARALLEL_KPI, name, key, extraLabels };
}

/**
 * startSequentialKpi
 * @param name - the name of the KPI. Used to match the end event
 */
export function startSequentialKpi(name) {
    return { type: START_SEQUENTIAL_KPI, name };
}

/**
 * endSequentialKpi
 * @param name - the name of the KPI. Used to match the end event
 * @param fieldsObject (optional) further field/value pairs describing the hit
 */
export function endSequentialKpi(name) {
    return { type: END_SEQUENTIAL_KPI, name };
}

/**
 * Start the overall clock for a continuous KPI, and start listening to resume
 * events which will start a cycle or complete events which will stop the
 * overall clock and send an analytic.
 * @param name - the name of the KPI. Used to match its end event and its
 * children cycles.
 */
export function initialiseContinuousKpi(name) {
    return { type: INITIALISE_CONTINUOUS_KPI, name };
}

/**
 * Start the clock of a child cycle. Can only be used if the KPI has been
 * initialised, has not been completed and if a cycle is not currently running.
 * @param name - the name of the KPI. Used to match its end event and its parent
 * overall timer.
 */
export function resumeContinuousKpi(name) {
    return { type: RESUME_CONTINUOUS_KPI, name };
}

/**
 * Start the clock of a child cycle. Can only be used if the KPI has been
 * initialised, has not been completed and if a cycle is currently running.
 * @param name - the name of the KPI. Used to match its start event and its
 * parent overall timer.
 */
export function pauseContinuousKpi(name) {
    return { type: PAUSE_CONTINUOUS_KPI, name };
}

/**
 * Stop the overall clock for a continuous KPI. Can only be used if the KPI has
 * been initialised and has not already been completed. If this is fired while a
 * child cycle is running, the child will be paused and its time added to the
 * runningTotal before completing the KPI.
 * @param name - the name of the KPI. Used to match its start event and its
 * parent overall timer.
 * @param fieldsObject (optional) further field/value pairs describing the hit
 */
export function completeContinuousKpi(name) {
    return { type: COMPLETE_CONTINUOUS_KPI, name };
}

/**
 * Create an action to record a timing metric
 * @param category the category of the metric (e.g. request)
 * @param variable the metric variable (e.g. login)
 * @param timeMillis the time to record in milliseconds
 */
export function timingMetric(category, variable, timeMillis) {
    return { type: TIMING_METRIC, category, variable, timeMillis };
}

/**
 * Set whether the page has become hidden or visible - i.e. whether the app is
 * not in the active tab, the device has been locked etc.
 * @param hidden if true, the app has become not visible
 */
export function setPageVisibility(hidden) {
    return { type: SET_PAGE_VISIBILITY, hidden };
}
